import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { withStyles } from 'vopay';
import { RailzService, getRailzServiceName } from '@/components/Intergrations/AccountingSoftware/railzServices';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { IntegrationCardData } from './types';
import styles from './useDisconnectRequiredDialog.module.scss';

const cn = withStyles(styles);

/**
 * An account can have only one accounting software connected at a time, and disconnecting is a
 * support-side operation. Clicking a second ERP card explains that, names the software already
 * connected, and offers to raise the ticket.
 */
export default function useDisconnectRequiredDialog() {
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UseDisconnectRequiredDialog' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  return async function openDisconnectRequiredDialog(integration: IntegrationCardData, connectedService: RailzService) {
    const connectedSoftware = getRailzServiceName(connectedService);

    const result = await alertDialog.caution(
      t('title', { defaultValue: 'Accounting software already connected' }),
      t('description', {
        defaultValue:
          'Your account is currently connected to {{connectedSoftware}}. Only one accounting software can be connected at a time. To connect {{integration}}, please contact our support team to disconnect {{connectedSoftware}} first.',
        connectedSoftware,
        integration: integration.name,
      }),
      {
        className: cn('dialog'),
        abortLabel: tGlobal('Buttons.close'),
        confirmLabel: tGlobal('Buttons.contactSupport'),
      },
    );

    if (result !== 'confirm') return;

    history.push({
      pathname: '/create-tickets',
      // The ticket form caps the subject at 50 characters, so keep it short
      state: {
        ticketSubject: t('ticketSubject', {
          defaultValue: 'Disconnect {{connectedSoftware}} integration',
          connectedSoftware,
        }),
      },
    });
  };
}
